/**
 * Copyright © 2026 Adnuntius AS.
 *
 * Rollbar client. Replaces the old window integration with the official
 * `rollbar` package, keeping the same method surface
 * (configure / error / warning / info / debug) used across the app.
 */
import {Injectable} from '@angular/core';
import Rollbar from 'rollbar';
import * as _ from 'lodash';

import BUILD_INFO from '../../gen-config/build-info';
import {ApiConfig} from '../api/api';

const ROLLBAR_ACCESS_TOKEN = '61a9e73004694142a31daa80928bde94';

/**
 * When true, Rollbar reports from local UI / localhost API env as well.
 * Leave false for normal development (avoids noise in Rollbar).
 */
export const ROLLBAR_ENABLED_ON_LOCALHOST = false;

/** Field names scrubbed from Rollbar payloads (aligned with AdnLog / exception handler). */
export const ROLLBAR_SCRUB_FIELDS = [
  'refresh_token',
  'access_token',
  'auth_token',
  'Authorization',
  'authorization',
  'password',
  'passwd'
];

/**
 * Known noisy messages that should not reach Rollbar (uibModal / ui-router transitions).
 * Shared with ExceptionHandlerService.
 */
export const ROLLBAR_IGNORED_MESSAGES = [
  'transition superseded',
  'Possibly unhandled rejection: transition superseded',
  'Possibly unhandled rejection: backdrop click',
  'Possibly unhandled rejection: undefined',
  'Possibly unhandled rejection: escape key press'
];

const ROLLBAR_IGNORED_STATUS_CODES = [401, 403, 404, 422];
const ROLLBAR_IGNORED_REJECTION_TYPES = [2, 3, 5];

function isLocalhostEnv(): boolean {
  return ApiConfig.obtain().getEnv() === 'localhost';
}

function isRollbarEnabledForCurrentEnv(): boolean {
  return ROLLBAR_ENABLED_ON_LOCALHOST || !isLocalhostEnv();
}

function messageFromArgs(args: Rollbar.LogArgument[]): string {
  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (_.isString(arg)) {
      return arg;
    }
    if (_.isError(arg) && arg.message) {
      return arg.message;
    }
    if (arg && _.isString((arg as any).message)) {
      return (arg as any).message;
    }
  }
  return '';
}

function shouldIgnoreRollbarItem(args: Rollbar.LogArgument[], item: Rollbar.Dictionary): boolean {
  const message = messageFromArgs(args)
    || _.get(item, ['body', 'message', 'body'], '')
    || _.get(item, ['body', 'trace', 'exception', 'message'], '')
    || '';
  if (_.isString(message) && message.indexOf('superseded') > -1) {
    return true;
  }

  for (let i = 0; i < args.length; i++) {
    const arg: any = args[i];
    if (!arg || !_.isObject(arg)) {
      continue;
    }
    const status = _.get(arg, ['status']);
    if (ROLLBAR_IGNORED_STATUS_CODES.indexOf(status) > -1) {
      return true;
    }
    if (_.get(arg, ['detail', 'status']) === 404) {
      return true;
    }
    const rejectionType = _.get(arg, ['type']);
    if (ROLLBAR_IGNORED_REJECTION_TYPES.indexOf(rejectionType) > -1) {
      return true;
    }
  }
  return false;
}

@Injectable({
  providedIn: 'root'
})
export class AdnRollbarService {
  private readonly client: Rollbar;

  constructor() {
    // Default V8 limit is 10 frames; Angular template / DI stacks are deeper.
    if (typeof Error !== 'undefined' && (Error as any).stackTraceLimit < 50) {
      (Error as any).stackTraceLimit = 50;
    }

    const apiId = ApiConfig.obtain().getId() || 'unknown';
    const codeVersion = BUILD_INFO.gitSha || 'unknown';
    this.client = new Rollbar({
      accessToken: ROLLBAR_ACCESS_TOKEN,
      captureUncaught: true,
      captureUnhandledRejections: true,
      enabled: isRollbarEnabledForCurrentEnv(),
      scrubFields: ROLLBAR_SCRUB_FIELDS,
      ignoredMessages: [...ROLLBAR_IGNORED_MESSAGES, /superseded/i],
      checkIgnore: (_isUncaught, args, item) => shouldIgnoreRollbarItem(args || [], item || {}),
      payload: {
        environment: String(apiId).replace(/_adn/g, ''),
        client: {
          javascript: {
            code_version: codeVersion
          }
        }
      }
    });
  }

  configure(options: any): any {
    return this.client.configure(options);
  }

  /**
   * Attach the signed-in user (and current network) for Rollbar grouping/filtering.
   */
  syncPerson(userProfile: {get: (prop?: string) => any}, networkProfile?: {get: (prop?: string) => any; getNetworkId?: () => string}): void {
    const userId = userProfile?.get('userId');
    if (!userId) {
      this.clearPerson();
      return;
    }
    const username = userProfile.get('username') || undefined;
    const networkId = networkProfile?.getNetworkId
      ? networkProfile.getNetworkId()
      : networkProfile?.get('tagId');
    this.client.configure({
      payload: {
        person: {
          id: String(userId),
          username,
          email: username,
          networkId: networkId || undefined,
          networkName: networkProfile?.get('networkName') || undefined
        }
      }
    });
  }

  clearPerson(): void {
    this.client.configure({
      payload: {
        person: {
          id: null
        }
      }
    });
  }

  critical(...args: any[]): any {
    return this.client.critical(...args);
  }

  error(...args: any[]): any {
    return this.client.error(...args);
  }

  warning(...args: any[]): any {
    return this.client.warning(...args);
  }

  info(...args: any[]): any {
    return this.client.info(...args);
  }

  debug(...args: any[]): any {
    return this.client.debug(...args);
  }
}
